import { disableScrolling, enableScrolling } from './utils';

/**
 * Shared drawer controller for the off-canvas panels rendered by the `components/drawer` Edge shell.
 *
 * Centralises the open/close lifecycle every drawer used to hand-roll: `.open` toggle, aria-hidden /
 * inert, scroll lock (shared, ref-counted), focus storage + restore, Escape-to-close, and
 * overlay/close-button binding. Focus trapping relies on `inert` on the root (set while closed) — no
 * manual tabindex juggling.
 *
 * Domain behaviour stays in the caller via the `onOpen` / `onClose` hooks (e.g. fetching + injecting
 * SSR content, refreshing the cart). Returns null when the element can't be found.
 */
export type DrawerController = {
  readonly el: HTMLElement;
  open: () => void;
  close: () => void;
  isOpen: () => boolean;
};

type DrawerOptions = {
  /** Called after the drawer opens (e.g. load content). */
  onOpen?: (drawer: DrawerController) => void;
  /** Called after the drawer closes (e.g. refresh the cart). */
  onClose?: (drawer: DrawerController) => void;
  /** Close-button selector scoped to the drawer (default '.js-drawer-close'). */
  closeSelector?: string;
  /** Overlay selector scoped to the drawer (default '.js-drawer-overlay'). */
  overlaySelector?: string;
};

export function createDrawer(
  target: HTMLElement | string,
  options: DrawerOptions = {},
): DrawerController | null {
  const el = typeof target === 'string' ? document.getElementById(target) : target;
  if (!el) {
    return null;
  }

  let lastFocused: HTMLElement | null = null;

  const isOpen = () => el.classList.contains('open');

  const controller: DrawerController = {
    el,
    isOpen,
    open() {
      if (isOpen()) {
        return;
      }
      lastFocused = document.activeElement as HTMLElement | null;
      el.classList.add('open');
      el.removeAttribute('aria-hidden');
      el.removeAttribute('inert');
      disableScrolling();
      el.focus({ preventScroll: true });
      options.onOpen?.(controller);
    },
    close() {
      if (!isOpen()) {
        return;
      }
      el.classList.remove('open');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
      enableScrolling();
      if (lastFocused) {
        lastFocused.focus({ preventScroll: true });
        lastFocused = null;
      }
      options.onClose?.(controller);
    },
  };

  el.querySelectorAll<HTMLElement>(options.closeSelector ?? '.js-drawer-close')
    .forEach((btn) => btn.addEventListener('click', () => controller.close()));

  el.querySelector<HTMLElement>(options.overlaySelector ?? '.js-drawer-overlay')
    ?.addEventListener('click', () => controller.close());

  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape' && isOpen()) {
      controller.close();
    }
  });

  return controller;
}
